import React from 'react';
import { Box, Typography, Tooltip, Chip } from '@material-ui/core';
import { useTimelineViewStyles } from '../styles/timeline-view.styles';
import { TimeMarker, ProcessedMilestone } from '../types';
import { getFullDateTimeLabel } from '../utils/timeline-processing';
import { MilestoneWithLevel, MilestoneLayoutConfig } from '../types';
import { 
  calculateMilestonePosition, 
  createMilestoneLayoutConfig,
  calculateSmartPositioning
} from '../utils/positioning';

/**
 * Props interface for the MilestoneChip component
 */
interface MilestoneChipProps {
  /** Milestone object with level information for positioning */
  milestone: MilestoneWithLevel;
  /** Layout configuration for milestone positioning calculations */
  layoutConfig: MilestoneLayoutConfig;
  /** Optional callback for milestone click events */
  onMilestoneClick?: (milestone: ProcessedMilestone) => void;
  /** Optional array of currently selected milestones */
  selectedMilestones?: ProcessedMilestone[];
}
/**
 * MilestoneChip Component
 * 
 * Renders a single milestone chip with an interactive tooltip containing
 * milestone details. Features color-coded styling and precise positioning
 * based on timestamp and level configuration.
 * 
 * @component
 * @param props - The component props
 * @param props.milestone - Milestone data with level and positioning info
 * @param props.layoutConfig - Configuration for milestone layout calculations
 * 
 * @returns A positioned milestone chip with tooltip functionality
 */
export const MilestoneChip = React.memo(({ 
  milestone, 
  layoutConfig, 
  onMilestoneClick, 
  selectedMilestones 
}: MilestoneChipProps) => {
  const classes = useTimelineViewStyles();
  const positionStyle = calculateMilestonePosition(milestone, layoutConfig);
  const [isHovered, setIsHovered] = React.useState(false);
  
  // Check if this milestone is selected
  const isSelected = selectedMilestones?.some(selected => 
    selected.timestamp === milestone.timestamp && selected.name === milestone.name
  );
  
  // Use the reusable smart positioning function
  const leftPercent = parseFloat(positionStyle.left as string);
  const { baseTransform, hoverTransform } = calculateSmartPositioning(leftPercent);

  // Handle milestone click
  const handleClick = React.useCallback(() => {
    if (onMilestoneClick) {
      onMilestoneClick(milestone);
    }
  }, [onMilestoneClick, milestone]);

  // Subtle hover styles for better UX
  const hoverStyle = isHovered ? {
    transform: hoverTransform,
    zIndex: 1000,
    transition: 'all 0.3s ease'
  } : {
    transform: baseTransform,
    transition: 'all 0.3s ease'
  };

  const chipHoverStyle = isHovered ? {
    transform: 'scale(1.08)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.6)',
    zIndex: 1001,
    transition: 'all 0.3s ease'
  } : {
    transition: 'all 0.3s ease'
  };
  
  return (
    <Tooltip
      title={
        <Box className={classes.tooltipContent}>
          <Typography variant="subtitle2" className={classes.tooltipTitle}>
            {milestone.name}
          </Typography>
          <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }} className={classes.tooltipContent}>
            {getFullDateTimeLabel(new Date(milestone.timestamp || -1))}
          </Typography>
          {milestone.description && (
            <Typography variant="body2" style={{ marginTop: 4, color: 'rgba(255,255,255,0.9)' }}>
              {milestone.description}
            </Typography>
          )}
          {milestone.metadata && milestone.metadata?.program && ( 
            <Typography variant="body2" style={{ marginTop: 4, color: 'rgba(255,255,255,0.9)' }}  className={classes.tooltipContent}>
              {`Program : ${milestone.metadata?.program}`}
            </Typography>)}
          {milestone.metadata && milestone.metadata?.subProgram && ( 
            <Typography variant="body2" style={{ marginTop: 4, color: 'rgba(255,255,255,0.9)' }}  className={classes.tooltipContent}>
              {`Sub Program : ${milestone.metadata?.subProgram}`}
            </Typography>)}
        </Box>
      }
      placement="top"
      arrow
    >
      <Box
        className={classes.milestone}
        style={{
          ...positionStyle,
          ...hoverStyle
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
      >
        <Chip
          label={milestone.name}
          size="small"
          className={classes.milestoneChip}
          style={{ 
            backgroundColor: milestone.color,
            color: 'white',
            fontWeight: 600,
            boxShadow: isSelected ? '0 0 0 3px rgba(33, 150, 243, 0.5)' : '0 2px 8px rgba(0,0,0,0.15)',
            border: isSelected ? '2px solid #2196f3' : '2px solid white',
            fontSize: '0.75rem',
            height: '24px',
            cursor: 'pointer',
            ...chipHoverStyle
          }}
        />
      </Box>
    </Tooltip>
  );
});

MilestoneChip.displayName = 'MilestoneChip';

/**
 * Props interface for the GridLine component
 */
interface GridLineProps {
  /** Horizontal position percentage for the grid line */
  position: number;
  /** Index for unique key generation */
  index: number;
}
/**
 * GridLine Component
 * 
 * Renders a single vertical grid line for visual alignment with pipeline
 * columns or time markers. Provides subtle visual guides for timeline
 * organization and element alignment.
 * 
 * @component
 * @param props - The component props
 * @param props.position - Horizontal position as percentage (0-100)
 * @param props.index - Unique index for React key prop
 * 
 * @returns A positioned vertical grid line element
 */
export const GridLine = React.memo(({ position, index }: GridLineProps) => {
  const classes = useTimelineViewStyles();
  
  return (
    <Box
      key={`grid-${index}`}
      className={classes.gridLine}
      style={{ 
        left: `${position}%`,
        position: 'absolute',
        opacity: 0.1
      }}
    />
  );
});

GridLine.displayName = 'GridLine';

/**
 * Props interface for the PipelineGridLines component
 */
interface PipelineGridLinesProps {
  /** Array of pipeline objects for grid line positioning */
  pipelines: any[];
}
/**
 * PipelineGridLines Component
 * 
 * Renders vertical grid lines aligned with the center of each pipeline column.
 * Calculates positions based on the number of pipelines and their equal
 * distribution across the available width.
 * 
 * @component
 * @param props - The component props
 * @param props.pipelines - Array of pipeline objects for positioning
 * 
 * @returns Multiple grid lines aligned with pipeline column centers
 */
export const PipelineGridLines = React.memo(({ pipelines }: PipelineGridLinesProps) => {
  return (
    <>
      {pipelines.map((pipeline, index) => {
        // Calculate the center position of each pipeline column
        const totalPipelines = pipelines.length;
        const columnWidthPercent = 100 / totalPipelines;
        const centerPercent = (index + 0.5) * columnWidthPercent;
        
        return (
          <GridLine
            key={`pipeline-grid-${index}`}
            position={centerPercent}
            index={index}
          />
        );
      })}
    </>
  );
});

PipelineGridLines.displayName = 'PipelineGridLines';

/**
 * Props interface for the TimeMarkerGridLines component
 */
interface TimeMarkerGridLinesProps {
  /** Array of time marker objects for grid line positioning */
  timeMarkers: TimeMarker[];
}
/**
 * TimeMarkerGridLines Component
 * 
 * Renders vertical grid lines aligned with time markers as a fallback
 * when pipeline-based alignment is not available. Uses pre-calculated
 * positions from time marker objects.
 * 
 * @component
 * @param props - The component props
 * @param props.timeMarkers - Array of time markers with position data
 * 
 * @returns Multiple grid lines aligned with time marker positions
 */
export const TimeMarkerGridLines = React.memo(({ timeMarkers }: TimeMarkerGridLinesProps) => {
  return (
    <>
      {timeMarkers.map((marker, index) => (
        <GridLine
          key={`time-marker-grid-${index}`}
          position={marker.position}
          index={index}
        />
      ))}
    </>
  );
});

TimeMarkerGridLines.displayName = 'TimeMarkerGridLines';

/**
 * Props interface for the MilestoneContainer component
 */
interface MilestoneContainerProps {
  /** Array of milestones with level information for positioning */
  milestonesWithLevels: MilestoneWithLevel[];
  /** Array of time markers for fallback grid alignment */
  timeMarkers: TimeMarker[];
  /** Optional array of pipelines for primary grid alignment */
  pipelines?: any[];
  /** Height of the milestone container in pixels */
  containerHeight: number;
  /** Minimum width of the container as CSS string */
  minWidth: string;
  /** Optional callback for milestone click events */
  onMilestoneClick?: (milestone: ProcessedMilestone) => void;
  /** Optional array of currently selected milestones */
  selectedMilestones?: ProcessedMilestone[];
}
/**
 * MilestoneContainer Component
 * 
 * Main container component that orchestrates the display of milestones
 * with appropriate grid lines and positioning. Handles both pipeline-based
 * and time marker-based alignment strategies. Manages layout configuration
 * and renders milestone chips with proper spacing and alignment.
 * 
 * @component
 * @param props - The component props
 * @param props.milestonesWithLevels - Milestones with positioning levels
 * @param props.timeMarkers - Time markers for fallback alignment
 * @param props.pipelines - Optional pipelines for primary alignment
 * @param props.containerHeight - Container height in pixels
 * @param props.minWidth - Minimum container width as CSS value
 * 
 * @returns A complete milestone display with grid lines and positioned chips
 */
export const MilestoneContainer = React.memo(({ 
  milestonesWithLevels, 
  timeMarkers, 
  pipelines, 
  containerHeight, 
  minWidth,
  onMilestoneClick,
  selectedMilestones
}: MilestoneContainerProps) => {
  const classes = useTimelineViewStyles();
  const layoutConfig = React.useMemo(() => createMilestoneLayoutConfig(), []);
  
  return (
    <Box className={classes.milestonesContainer} style={{ 
      height: containerHeight,
      marginBottom: '8px'
    }}>
      <Box style={{ 
        minWidth,
        width: '100%',
        position: 'relative',
        height: '100%'
      }}>
        {/* Grid lines - align with pipeline columns or time markers */}
        {pipelines && pipelines.length > 0 ? (
          <PipelineGridLines pipelines={pipelines} />
        ) : (
          <TimeMarkerGridLines timeMarkers={timeMarkers} />
        )}
        
        {/* Milestone chips */}
        {milestonesWithLevels.map((milestone, index) => (
          <MilestoneChip
            key={index}
            milestone={milestone}
            layoutConfig={layoutConfig}
            onMilestoneClick={onMilestoneClick}
            selectedMilestones={selectedMilestones}
          />
        ))}
      </Box>
    </Box>
  );
});

MilestoneContainer.displayName = 'MilestoneContainer';
